import { NothingToShow } from 'shared-ui';

import { useLocale } from 'hooks/useLocale/useLocale';
import { AppMessages } from 'i18n/messages';
import { BarPanel } from '../components/barPanel/BarPanel';
import { DenseTable } from '../components/denseTable/DenseTable';
import { DenseTableColumn } from '../components/denseTable/DenseTable.types';
import { SectionCard } from '../components/sectionCard/SectionCard';
import { StateChip } from '../components/stateChip/StateChip';
import { TimeSeriesPanel } from '../components/timeSeriesPanel/TimeSeriesPanel';

import * as Styled from './RetriesTab.styles';
import { RetriesQueueRow, RetriesTabProps } from './RetriesTab.types';

const QUEUE_STATES = ['idle', 'draining', 'paused'] as const;

const EMPTY_CELL = '—';

export const RetriesTab = ({
  attemptBars,
  isAttemptsLoading,
  retriesSeries,
  retriesSeriesLabels,
  isRetriesLoading,
  deferredSeries,
  deferredSeriesLabels,
  hasDeferredReasons,
  isDeferredLoading,
  hasPrometheus,
  deliveryQuantileSeries,
  deliveryQuantileLabels,
  sendQuantileSeries,
  sendQuantileLabels,
  isQuantilesLoading,
  rows,
  isRowsLoading,
  isError,
  onOpenQueue,
}: RetriesTabProps): JSX.Element => {
  const { formatMessage } = useLocale();

  const renderUnavailable = (title: string): JSX.Element => (
    <SectionCard title={title} bodyHeight={240}>
      <NothingToShow
        text={`${formatMessage({ id: AppMessages['dod.retries.requiresPrometheus'] })}. ${formatMessage({
          id: AppMessages['dod.retries.requiresPrometheus.detail'],
        })}`}
      />
    </SectionCard>
  );

  const columns: Array<DenseTableColumn<RetriesQueueRow>> = [
    {
      key: 'rcpTkd',
      width: 'minmax(140px, 1.4fr)',
      header: formatMessage({ id: AppMessages['dod.retries.column.rcpTkd'] }),
      render: (row) => `${row.rcpLabel} · ${row.tkdLabel}`,
    },
    {
      key: 'state',
      width: '150px',
      header: formatMessage({ id: AppMessages['dod.retries.column.state'] }),
      render: (row) => (
        <StateChip
          state={(QUEUE_STATES as readonly string[]).includes(row.state) ? (row.state as 'idle') : 'unknown'}
        />
      ),
    },
    {
      key: 'attempts',
      width: '110px',
      header: formatMessage({ id: AppMessages['dod.retries.column.attempts'] }),
      render: (row) => row.retryCountMax,
    },
    {
      key: 'depth',
      width: '90px',
      header: formatMessage({ id: AppMessages['dod.retries.column.depth'] }),
      render: (row) => row.depth,
    },
    {
      key: 'dlq',
      width: '80px',
      header: formatMessage({ id: AppMessages['dod.retries.column.dlq'] }),
      render: (row) => row.dlqDepth,
    },
    {
      key: 'lastError',
      width: 'minmax(120px, 2fr)',
      header: formatMessage({ id: AppMessages['dod.retries.column.lastError'] }),
      render: (row) => row.lastError || EMPTY_CELL,
    },
  ];

  return (
    <>
      <Styled.PanelRow>
        <BarPanel
          title={formatMessage({ id: AppMessages['dod.retries.panel.attempts'] })}
          data={attemptBars}
          layout="vertical"
          height={240}
          isLoading={isAttemptsLoading}
        />

        <SectionCard
          title={formatMessage({ id: AppMessages['dod.retries.panel.retriesPerMin'] })}
          bodyHeight={240}
          actions={
            <Styled.Caption>{formatMessage({ id: AppMessages['dod.retries.panel.attempts.caption'] })}</Styled.Caption>
          }
        >
          <TimeSeriesPanel
            title=""
            series={retriesSeries}
            seriesLabels={retriesSeriesLabels}
            area
            stacked
            height={200}
            isLoading={isRetriesLoading}
          />
        </SectionCard>
      </Styled.PanelRow>

      <Styled.PanelRow>
        <SectionCard
          title={formatMessage({ id: AppMessages['dod.retries.panel.deferred'] })}
          bodyHeight={240}
          actions={
            hasDeferredReasons ? undefined : (
              <Styled.Caption>
                {formatMessage({ id: AppMessages['dod.retries.panel.deferred.caption'] })}
              </Styled.Caption>
            )
          }
        >
          <TimeSeriesPanel
            title=""
            series={deferredSeries}
            seriesLabels={deferredSeriesLabels}
            area
            stacked={hasDeferredReasons}
            height={200}
            isLoading={isDeferredLoading}
          />
        </SectionCard>

        {hasPrometheus ? (
          <TimeSeriesPanel
            title={formatMessage({ id: AppMessages['dod.retries.panel.deliveryLatency'] })}
            series={deliveryQuantileSeries}
            seriesLabels={deliveryQuantileLabels}
            height={240}
            isLoading={isQuantilesLoading}
          />
        ) : (
          renderUnavailable(formatMessage({ id: AppMessages['dod.retries.panel.deliveryLatency'] }))
        )}
      </Styled.PanelRow>

      {hasPrometheus ? (
        <TimeSeriesPanel
          title={formatMessage({ id: AppMessages['dod.retries.panel.sendLatency'] })}
          series={sendQuantileSeries}
          seriesLabels={sendQuantileLabels}
          height={240}
          isLoading={isQuantilesLoading}
        />
      ) : (
        renderUnavailable(formatMessage({ id: AppMessages['dod.retries.panel.sendLatency'] }))
      )}

      {/* The shared table rows are white on the grey page; inside a white card they vanish. */}
      <Styled.TableTitle variant="h6">
        {formatMessage({ id: AppMessages['dod.retries.table.title'] })}
      </Styled.TableTitle>
      {isError ? (
        <div role="alert">
          <NothingToShow text={formatMessage({ id: AppMessages['dod.common.error'] })} />
        </div>
      ) : (
        <DenseTable<RetriesQueueRow>
          // A row opens its queue (the details panel on Kolejki); the rows carry no buttons.
          onClickRow={onOpenQueue}
          columns={columns}
          data={rows}
          isLoading={isRowsLoading}
          nothingToShowText={formatMessage({ id: AppMessages['dod.retries.empty'] })}
        />
      )}
    </>
  );
};
